/**
 * Service Worker Registration
 *
 * This file handles the registration and lifecycle management of the service worker.
 * It includes update notification and offline mode detection.
 */

import config from "./configs/env";

// Check if the service worker API is available
const isServiceWorkerSupported = "serviceWorker" in navigator;

// Check if the app is running on localhost
const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    window.location.hostname === "[::1]" ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

/**
 * Register the service worker
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Callback when service worker has been registered
 * @param {Function} options.onUpdate - Callback when service worker has been updated
 */
export function register(options = {}) {
  // Only register in production or if explicitly enabled in development
  if (
    (process.env.NODE_ENV === "production" || config.features.enablePWAInDevelopment) &&
    isServiceWorkerSupported
  ) {
    // The URL constructor is available in all browsers that support SW
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);

    // If the public URL is on a different origin than our page, don't register the service worker
    // This might happen if using a CDN to serve assets
    if (publicUrl.origin !== window.location.origin) {
      console.warn("Service worker not registered: different origin");
      return;
    }

    window.addEventListener("load", () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, options);

        // Add some additional logging to localhost
        navigator.serviceWorker.ready.then(() => {
          console.log(
            "This web app is being served cache-first by a service worker. " +
              "To learn more, visit https://cra.link/PWA"
          );
        });
      } else {
        // Not localhost. Just register service worker
        registerValidSW(swUrl, options);
      }
    });

    // Handle offline/online events
    setupOfflineDetection();
  }
}

/**
 * Register a valid service worker
 *
 * @param {string} swUrl - URL of the service worker script
 * @param {Object} options - Configuration options
 */
function registerValidSW(swUrl, options) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // Check for updates on page load
      registration.update().catch((error) => {
        console.warn("Service worker update check failed:", error);
      });

      // Set up periodic updates
      setInterval(() => {
        registration.update().catch((error) => {
          console.warn("Service worker periodic update failed:", error);
        });
      }, 60 * 60 * 1000); // Check for updates every hour

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === "installed") {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                "New content is available and will be used when all tabs for this page are closed."
              );

              // Dispatch event for update notification
              window.dispatchEvent(new CustomEvent("sw-update-available"));

              // Execute callback
              if (options && options.onUpdate) {
                options.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log("Content is cached for offline use.");

              // Execute callback
              if (options && options.onSuccess) {
                options.onSuccess(registration);
              }
            }
          } else if (installingWorker.state === "redundant") {
            console.warn("Service worker became redundant during installation");
          }
        };
      };
    })
    .catch((error) => {
      console.error("Error during service worker registration:", error);
      // Don't throw - allow the app to continue without service worker
    });
}

/**
 * Check if a service worker is valid
 *
 * @param {string} swUrl - URL of the service worker script
 * @param {Object} options - Configuration options
 */
function checkValidServiceWorker(swUrl, options) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { "Service-Worker": "script" },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get("content-type");
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf("javascript") === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, options);
      }
    })
    .catch(() => {
      console.log("No internet connection found. App is running in offline mode.");

      // Dispatch offline event
      window.dispatchEvent(new CustomEvent("app-offline"));
    });
}

/**
 * Set up offline detection
 */
function setupOfflineDetection() {
  // Initial check
  updateOnlineStatus();

  // Add event listeners
  window.addEventListener("online", updateOnlineStatus);
  window.addEventListener("offline", updateOnlineStatus);
}

/**
 * Update online status and dispatch events
 */
function updateOnlineStatus() {
  const isOnline = navigator.onLine;

  // Dispatch custom event
  window.dispatchEvent(new CustomEvent(isOnline ? "app-online" : "app-offline"));

  // Update body class for CSS styling
  if (isOnline) {
    document.body.classList.remove("offline-mode");
  } else {
    document.body.classList.add("offline-mode");
  }

  console.log(`App is ${isOnline ? "online" : "offline"}`);
}

/**
 * Unregister the service worker
 */
export function unregister() {
  if (isServiceWorkerSupported) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

/**
 * Send message to service worker to skip waiting
 */
export function forceUpdate() {
  if (isServiceWorkerSupported) {
    navigator.serviceWorker.ready.then((registration) => {
      if (registration.waiting) {
        // Send skip waiting message
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
      }
    });
  }
}

/**
 * Check if the app is running in offline mode
 *
 * @returns {boolean} Whether the app is offline
 */
export function isOffline() {
  return !navigator.onLine;
}

/**
 * Clear all service worker caches and unregister
 * Useful for troubleshooting cache issues
 */
export async function clearAllCachesAndUnregister() {
  try {
    // Clear all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
      console.log('All caches cleared');
    }

    // Unregister all service workers
    if (isServiceWorkerSupported) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(registration => registration.unregister()));
      console.log('All service workers unregistered');
    }

    return true;
  } catch (error) {
    console.error('Error clearing caches and unregistering service workers:', error);
    return false;
  }
}

export default {
  register,
  unregister,
  forceUpdate,
  isOffline,
};
